import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { useApiAuth } from "../hooks/useApiAuth";
import { useCompetitionRounds } from "../hooks/useCompetitionRounds";
import { apiUrl } from "../lib/api";
import { TeamSelector } from "./order-ticket/TeamSelector";
import { OrderControls } from "./order-ticket/OrderControls";
import { MarketDataGrid } from "./order-ticket/MarketDataGrid";
import { OrderSummary } from "./order-ticket/OrderSummary";
import { OrderActions } from "./order-ticket/OrderActions";
import { HomeTutorialModal, TutorialSlide } from "./home/HomeTutorialModal";
import type { OrderSide, TeamOption } from "./order-ticket/types";
import type { SeasonStanding } from "../types/market";
import { useLocalization } from "../lib/i18n";
import { resolveOrderTicketMarketState } from "../lib/regularSeasonRecord";

interface OrderTicketProps {
  competitionCode: string;
  seasonCode: string;
  poolCode: string;
  traderCode: string;
  standings: SeasonStanding[];
  initialSide: OrderSide;
  initialTeamCode: string | null;
  initialPrice?: number;
  initialQuantity?: number;
  editingOrderId?: number;
  tradingLocked?: boolean;
  onUpdateOrder?: (orderId: number, price: number, quantity: number) => Promise<void>;
  onClose: () => void;
  onOrderBooked?: () => void;
}

const buildTeamsUrl = (competitionCode: string, seasonCode: string, poolCode: string) => {
  const path = `/api/competitions/${competitionCode}/seasons/${seasonCode}/teams?pool_code=${encodeURIComponent(poolCode)}`;
  return apiUrl(path);
};
const TRADING_LOCKED_TITLE = "APPROVAL PENDING";
const TRADING_LOCKED_BODY = "Set your picks now — trading unlocks once you're confirmed.";

export const OrderTicket: React.FC<OrderTicketProps> = ({
  competitionCode,
  seasonCode,
  poolCode,
  traderCode,
  standings,
  initialSide,
  initialTeamCode,
  initialPrice,
  initialQuantity,
  editingOrderId,
  tradingLocked = false,
  onUpdateOrder,
  onClose,
  onOrderBooked,
}) => {
  // Auth
  const { isSignedIn: isAuthenticated } = useUser();
  const { acquireToken } = useApiAuth();
  const { t } = useLocalization();
  const effectiveTraderCode = traderCode.toUpperCase();

  // Fetch round labels from API
  const { roundLabels } = useCompetitionRounds();

  // State
  const [side, setSide] = useState<OrderSide>(initialSide);
  const [teamCode, setTeamCode] = useState<string>(initialTeamCode ?? "");
  const [quantity, setQuantity] = useState<number | null>(initialQuantity ?? null);
  const [price, setPrice] = useState<number>(initialPrice ?? 0);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [highlightPrice, setHighlightPrice] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [floorPriceWarning, setFloorPriceWarning] = useState<string | null>(null);


  // Team loading state
  const [teamOptions, setTeamOptions] = useState<TeamOption[]>([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);

  // Prediction state
  const [teamPredictedPoints, setTeamPredictedPoints] = useState<number | null>(null);

  // Refs
  const priceInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFloorPriceRef = useRef<number>(0);

  // Standings map for quick lookup
  const standingsMap = useMemo(
    () => new Map(standings.map((row) => [row.team_code, row])),
    [standings]
  );

  // Selected team data
  const selectedStanding = teamCode ? standingsMap.get(teamCode) : null;
  const ticketMarketState = resolveOrderTicketMarketState(selectedStanding);
  const selectedTeamOption = teamOptions.find((team) => team.code === teamCode);
  const floorPrice = selectedTeamOption?.floorPrice ?? selectedStanding?.market_floor_price ?? 0;

  // Market data
  const topBidPrice = selectedStanding?.top_bid_price ?? null;
  const topBidQuantity = selectedStanding?.top_bid_quantity ?? null;
  const topAskPrice = selectedStanding?.top_ask_price ?? null;
  const topAskQuantity = selectedStanding?.top_ask_quantity ?? null;

  // Load teams on mount
  useEffect(() => {
    const controller = new AbortController();
    const loadTeams = async () => {
      if (!isAuthenticated) {
        setTeamOptions([]);
        setTeamCode("");
        setTeamError("Sign in required to load teams.");
        return;
      }

      setIsLoadingTeams(true);
      setTeamError(null);

      try {
        const token = await acquireToken();
        if (!token) {
          setTeamError("Authentication required");
          return;
        }

        const response = await fetch(buildTeamsUrl(competitionCode, seasonCode, poolCode), {
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load teams (${response.status})`);
        }

        const payload: Array<{
          team_code: string;
          team_state: string;
          market_floor_price: number;
        }> = await response.json();

        // Filter out teams that are no longer tradeable (eliminated or champion)
        const activeTeams = payload
          .filter((item) => item.team_state !== "ELIMINATED" && item.team_state !== "CHAMPION")
          .map((item) => ({
            code: item.team_code,
            state: item.team_state,
            floorPrice: Number.isFinite(item.market_floor_price) ? item.market_floor_price : 0,
          }));

        setTeamOptions(activeTeams);
        if (activeTeams.length > 0) {
          setTeamCode((current) =>
            initialTeamCode && activeTeams.some((t) => t.code === initialTeamCode)
              ? initialTeamCode
              : current && activeTeams.some((t) => t.code === current)
                ? current
                : activeTeams[0].code
          );
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Failed to load active teams", error);
          setTeamOptions([]);
          setTeamCode("");
          setTeamError("Could not load active teams.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingTeams(false);
        }
      }
    };

    loadTeams();
    return () => controller.abort();
  }, [competitionCode, initialTeamCode, isAuthenticated, poolCode, seasonCode, acquireToken]);

  // Update side when initialSide changes
  useEffect(() => {
    setSide(initialSide);
  }, [initialSide]);

  // Update teamCode when initialTeamCode changes
  useEffect(() => {
    if (initialTeamCode) {
      setTeamCode(initialTeamCode);
    }
  }, [initialTeamCode]);

  // Show warning toast when floor price increases and current price becomes invalid
  useEffect(() => {
    const prevFloor = previousFloorPriceRef.current;
    const minPrice = Math.ceil(floorPrice) + 1;
    
    // Only show warning if floor increased and price is now below minimum
    if (prevFloor > 0 && floorPrice > prevFloor && price < minPrice) {
      setFloorPriceWarning(`Floor price increased to B${Math.ceil(floorPrice)}. Please adjust your price to at least B${minPrice}.`);
      // Auto-dismiss after 5 seconds
      setTimeout(() => setFloorPriceWarning(null), 5000);
    }
    
    previousFloorPriceRef.current = floorPrice;
  }, [floorPrice, price]);

  // Fetch predicted points for selected team
  useEffect(() => {
    if (!isAuthenticated || !teamCode || !poolCode) {
      setTeamPredictedPoints(null);
      return;
    }

    const fetchTeamPoints = async () => {
      try {
        const token = await acquireToken();
        if (!token) {
          setTeamPredictedPoints(null);
          return;
        }

        const url = apiUrl(`/api/scenarios/team-points/${teamCode}?pool_code=${poolCode}`);

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.total_predicted !== null && data.total_predicted !== undefined) {
            setTeamPredictedPoints(data.total_predicted);
          } else {
            setTeamPredictedPoints(null);
          }
        } else {
          setTeamPredictedPoints(null);
        }
      } catch (error) {
        console.error("Failed to fetch team points:", error);
        setTeamPredictedPoints(null);
      }
    };

    fetchTeamPoints();
  }, [isAuthenticated, teamCode, poolCode, acquireToken]);

  // Set initial valid price when floor price becomes available (only if price is 0 or invalid)
  useEffect(() => {
    if (floorPrice > 0) {
      const minPrice = Math.ceil(floorPrice) + 1;
      // Only auto-set if price is 0 or below minimum (initial state)
      if (price === 0 || price < minPrice) {
        setPrice(minPrice);
      }
    }
  }, [floorPrice]); // Intentionally exclude price to avoid overriding user input

  // Real-time price validation (computed, not state)
  const priceError = useMemo(() => {
    const intPrice = Math.round(price);
    const minPrice = Math.ceil(floorPrice) + 1;
    const maxPrice = 99;
    
    if (intPrice <= 0) return "Price must be positive";
    if (intPrice < minPrice) return `Price must be at least ${minPrice}`;
    if (intPrice > maxPrice) return "Price cannot exceed 99";
    if (!Number.isInteger(price)) return "Price must be a whole number";
    
    return undefined;
  }, [price, floorPrice]);

  // Validation
  const validate = useCallback(() => {
    const newErrors: { [key: string]: string } = {};

    if (!teamCode) {
      newErrors.team = "Select a team";
    }

    if (quantity === null || quantity < 1 || quantity > 100) {
      newErrors.quantity = "Quantity must be 1-100";
    }

    // Use the computed priceError for validation
    if (priceError) {
      newErrors.price = priceError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [teamCode, quantity, priceError]);

  const canSubmit = Boolean(
    !isLoadingTeams &&
    !teamError &&
    teamCode &&
    quantity !== null &&
    quantity >= 1 &&
    price &&
    !priceError &&
    effectiveTraderCode &&
    poolCode &&
    isAuthenticated &&
    !tradingLocked &&
    !ticketMarketState.isTradingClosed &&
    !submitting
  );

  // Handle submit
  const handleSubmit = async () => {
    if (ticketMarketState.isTradingClosed) {
      setErrors({
        submit: ticketMarketState.reasonKey
          ? t(ticketMarketState.reasonKey)
          : t("market.tradingClosed.unavailable"),
      });
      return;
    }
    if (tradingLocked) {
      setErrors({ submit: `${TRADING_LOCKED_TITLE}. ${TRADING_LOCKED_BODY}` });
      return;
    }
    if (!validate() || !canSubmit) return;

    const parsedPrice = Math.round(price);

    setSubmitting(true);
    setConfirmation(null);
    setErrors({});

    try {
      // If editing an existing order, use the update API
      if (editingOrderId && onUpdateOrder) {
        try {
          await onUpdateOrder(editingOrderId, parsedPrice, quantity!);
          const priceStr = ` @ B${parsedPrice}`;
          setConfirmation(`Order updated: ${side} ${quantity} ${teamCode}${priceStr}`);
          onOrderBooked?.();
          setTimeout(() => onClose(), 1500);
          return;
        } catch (updateError) {
          console.error("Failed to update order", updateError);
          throw new Error(updateError instanceof Error ? updateError.message : "Failed to update order. Please try again.");
        }
      }

      // Otherwise, create a new order
      const token = await acquireToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      const response = await fetch(apiUrl("/api/orders"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          competition_code: competitionCode,
          season_code: seasonCode,
          team_code: teamCode,
          side,
          quantity,
          price: parsedPrice,
          trader_code: effectiveTraderCode,
          pool_code: poolCode,
          order_type: "LIMIT",
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        let errorMessage = text || `Failed to submit order (${response.status})`;

        try {
          const errorData = JSON.parse(text);
          if (
            errorData.detail?.error === "INSUFFICIENT_DEPTH" &&
            errorData.detail?.max_quantity !== undefined
          ) {
            errorMessage = `${errorData.detail.message}`;
          } else if (errorData.detail?.error === "SELF_CROSSING_ORDER") {
            errorMessage = errorData.detail.message;
          } else if (typeof errorData.detail === "string") {
            errorMessage = errorData.detail;
          }
        } catch {
          // Not JSON, use text as-is
        }

        throw new Error(errorMessage);
      }

      // Show confirmation briefly then close
      const priceStr = ` @ B${Math.round(price)}`;
      setConfirmation(`Order accepted: ${side} ${quantity} ${teamCode}${priceStr}`);
      onOrderBooked?.();

      // Close the form after a brief delay to show confirmation
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error("Failed to submit order", error);
      setErrors({ submit: error instanceof Error ? error.message : "Order failed" });
    } finally {
      setSubmitting(false);
    }
  };

  // Market data click handlers - sets price and highlights input
  const handleMarketDataClick = (value: number | null) => {
    if (value != null) {
      const intPrice = Math.round(value);
      setPrice(intPrice);
      setHighlightPrice(true);
      priceInputRef.current?.focus();
      priceInputRef.current?.select();

      // Clear highlight after animation
      setTimeout(() => setHighlightPrice(false), 300);
    }
  };

  const howToSlides: TutorialSlide[] = [
    {
      title: "Buy vs. Sell: Betting on the Bracket",
      body: `
      Think of a "share" as a bet on the team's final score (0-100).

      **BUY** if you think the team will go deeper in the playoffs than the current price suggests.
      **SELL** if you think they will get knocked out sooner than expected.

      You are simply trading your prediction against other players.
      `,
      image: "/img/tutorial/order/1_buyorsell.jpg",
    },
    {
      title: "Quantity & Price: Set Your Number",
      body: `
      **Quantity** is just how many shares you want to trade.

      **Price** is the number you are willing to pay or accept. The trade only happens if another player agrees to your price.
      `,
      image: "/img/tutorial/order/2_speedvsprice.jpg",
    },
    {
      title: "The Floor: Points are Banked Forever",
      body: `
      A team can never trade for less than the points they have already earned.

      The **Floor** is the "safe" zone. These points are locked in based on past wins.
      
      You are only trading based on **future potential points**. As teams win rounds, the Floor rises and the risk of the price dropping gets smaller.
      `,
      image: "/img/tutorial/order/3_floor.jpg",
    },
    {
      title: "The Book: Reading the Room",
      body: `
      The Order Book just shows you what other players are waiting to do.

      - **Best Bid:** The highest price someone is waiting to pay *you*.
      - **Best Ask:** The cheapest price someone is waiting to sell to *you*.

      If the gap between them (the spread) is wide, people disagree on how good the team is!
      `,
      image: "/img/tutorial/order/4_readingthebook.jpg",
    },
    {
      title: "How to Calculate Value",
      body: `
      Don't overcomplicate the price. 
      
      **Price = Current Points + Expected Future Points.**

      If a team costs **50**, the market predicts they will earn 50 points total. 
      
      If you calculate their path through the bracket results in **80** points, buying at 50 is a winning strategy.
      `,
      image: "/img/tutorial/order/5_value.jpg",
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 md:items-center md:px-4 md:py-8"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!showHowTo) {
          onClose();
        }
      }}
    >
      <div
        ref={containerRef}
        className="flex max-h-[92vh] w-full flex-col rounded-t-3xl border border-border bg-background-surface font-sans shadow-2xl md:max-h-[90vh] md:max-w-[34rem] md:rounded-2xl lg:max-w-[38rem]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center py-2 md:hidden">
          <div className="h-1 w-12 rounded-full bg-foreground-faint" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-3 pt-1 md:px-6">
          <h2 className="font-display text-[32px] font-bold uppercase text-foreground">
            Place Order
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-2 text-3xl leading-none text-foreground-subtle hover:text-foreground"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Confirmation Toast */}
        {confirmation && (
          <div className="mx-4 mt-3 rounded border border-success/50 bg-success/10 px-3 py-2 text-xs text-success">
            {confirmation}
          </div>
        )}

        {/* Submit Error Toast */}
        {errors.submit && (
          <div className="mx-4 mt-3 px-3 py-2 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-xs">
            {errors.submit}
          </div>
        )}

        {tradingLocked && (
          <div className="mx-4 mt-3 rounded border border-amber-600/60 bg-amber-900/20 px-3 py-2 text-xs text-amber-200">
            <p className="font-semibold uppercase tracking-wide">{TRADING_LOCKED_TITLE}</p>
            <p>{TRADING_LOCKED_BODY}</p>
          </div>
        )}

        {ticketMarketState.isTradingClosed && (
          <div className="mx-4 mt-3 rounded border border-danger/50 bg-danger/10 px-3 py-2 text-xs text-danger" role="status">
            {ticketMarketState.reasonKey
              ? t(ticketMarketState.reasonKey)
              : t("market.tradingClosed.unavailable")}
          </div>
        )}

        {/* Floor Price Warning Toast */}
        {floorPriceWarning && (
          <div className="mx-4 mt-3 px-3 py-2 bg-amber-900/30 border border-amber-700/50 rounded text-amber-300 text-xs flex items-center justify-between">
            <span>{floorPriceWarning}</span>
            <button
              onClick={() => setFloorPriceWarning(null)}
              className="ml-2 text-amber-400 hover:text-amber-200"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="space-y-4 overflow-y-auto px-5 pb-5 pt-1 md:px-6">
          {/* Team Selector */}
          <TeamSelector
            teamCode={teamCode}
            standingsMap={standingsMap}
            roundLabels={roundLabels}
            isLoading={isLoadingTeams}
            error={teamError}
          />

          {/* Market Data Grid */}
          <MarketDataGrid
            topBidPrice={topBidPrice}
            topBidQuantity={topBidQuantity}
            topAskPrice={topAskPrice}
            topAskQuantity={topAskQuantity}
            onPriceClick={handleMarketDataClick}
          />

          {/* Order Controls: Side, Qty, Price */}
          <fieldset disabled={ticketMarketState.isTradingClosed} className="contents">
            <OrderControls
              side={side}
              quantity={quantity}
              price={price}
              floorPrice={floorPrice}
              highlightPrice={highlightPrice}
              disableSide={!!editingOrderId}
              priceError={priceError}
              onSideChange={setSide}
              onQuantityChange={setQuantity}
              onPriceChange={setPrice}
              priceInputRef={priceInputRef as React.RefObject<HTMLInputElement>}
            />
          </fieldset>

          {/* Validation errors */}
          {(errors.quantity || priceError) && (
            <div className="flex flex-wrap gap-3 text-xs text-danger">
              {errors.quantity && <span>{errors.quantity}</span>}
              {priceError && <span>{priceError}</span>}
            </div>
          )}

          {/* Order Summary */}
          <OrderSummary
            side={side}
            quantity={quantity}
            price={price}
            teamCode={teamCode}
            teamPredictedPoints={teamPredictedPoints}
          />

          {/* Action Buttons */}
          <OrderActions
            side={side}
            submitLabel={editingOrderId ? "Update Order" : "Submit Order"}
            canSubmit={canSubmit}
            submitting={submitting}
            onSubmit={handleSubmit}
            onCancel={onClose}
          />

        </div>
      </div>
      <HomeTutorialModal
        isOpen={showHowTo}
        onClose={() => setShowHowTo(false)}
        title="How to place an order"
        slides={howToSlides}
      />
    </div>
  );
};
